import {
  Dispatch,
  memo,
  SetStateAction,
  useCallback,
  useEffect,
  useState
} from 'react';
import dayjs from 'dayjs';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import Avatar from '@mui/material/Avatar';
import IconButton from '@mui/material/IconButton';
import { useTranslation } from 'react-i18next';

import type { UnreadNotification } from 'api/messageCommunication/types';
import { LikeButtonSVG, RejectButtonSVG } from 'components/shared/Icons';
import { AccessElements, NotificationKey } from 'helpers/app.config';
import { useGlobalContext } from 'context/GlobalContext';
import ModalSendFormSurveys from 'pages/Surveys/components/ModalSendForm';

import useNotificationActions, {
  DestinyStatus
} from '../hooks/useNotificationActions';

const styles = {
  root: {
    display: 'flex',
    padding: '16px',
    backgroundColor: 'white',
    borderRadius: '8px',
    border: '0.5px solid #ACACAC',
    flexDirection: {
      xs: 'column',
      sm: 'row'
    },
    justifyContent: 'space-between'
  },
  avatarContainer: {
    display: 'flex',
    alignItems: 'center'
  },
  icon: {
    marginRight: '16px',
    marginBottom: {
      xs: '10px',
      sm: '0px'
    }
  },
  buttonBase: {
    marginTop: 2 * 8,
    cursor: 'pointer'
  },
  doneIcon: {
    position: 'absolute',
    top: 8,
    right: 8,
    color: '#1D6FCC'
  },
  dateContainer: {
    display: 'flex',
    flexDirection: 'column' as const,
    alignItems: {
      xs: 'center',
      sm: 'flex-end'
    },
    marginTop: {
      xs: '10px',
      sm: '0px'
    },
    minWidth: '80px'
  },
  actionsButtonsContainer: {
    flex: '1',
    display: 'flex',
    alignItems: 'flex-end'
  },
  btnSendSurvey: {
    fontSize: '12px',
    background: '#1d6fcc',
    borderRadius: '8px',
    color: 'white',
    position: 'absolute' as const,
    right: '30px'
  }
};

type Props = Partial<UnreadNotification> & {
  handleReadNotification: (notificationId: number) => void;
  notificationKey: NotificationKey;
  needsAttention: boolean;
  ticketIdActive: number;
  setTicketId: Dispatch<SetStateAction<number | string>>;
  setNotificationId: Dispatch<SetStateAction<number>>;
  setDestinyStatus: Dispatch<SetStateAction<DestinyStatus>>;
  handleClose: () => void;
  setOpenLogoutModal?: () => void;
};

function splitByTags(text: string): string[] {
  const tagsContent: string[] = [];
  const regex = /<b>(.*?)<\/b>/g;
  let match;

  let start = 0;

  // eslint-disable-next-line no-cond-assign
  while ((match = regex.exec(text)) !== null) {
    const tagContent = match[1];
    const startOfMatch = text?.indexOf(match[0], start);

    if (startOfMatch > start) {
      tagsContent.push(text?.substring(start, startOfMatch));
    }

    tagsContent.push(tagContent);

    start = startOfMatch + match[0].length;
  }

  if (start < text?.length) {
    tagsContent.push(text?.substring(start));
  }

  return tagsContent;
}

/**
 * Notification Component
 *
 * Represents a notification component that displays information and actions for a notification.
 *
 * @component
 * @param {Object} Props The properties for the notification component.
 * @param {Function} Props.advise The advice message for the notification.
 * @param {Date} Props.emmitedDate The date when the notification was emitted.
 * @param {Date} Props.expireDate The expiration date for the notification.
 * @param {Function} Props.handleClose Function to handle closing the notification.
 * @param {Function} Props.handleReadNotification Function to handle marking the notification as read.
 * @param {string} Props.iconUrl The URL for the notification icon.
 * @param {string} Props.message The main message content of the notification.
 * @param {boolean} [Props.needsAttention=true] Flag indicating if the notification needs attention.
 * @param {number} Props.notificationId The unique identifier for the notification.
 * @param {NotificationKey}[Props.notificationKey=NotificationKey.Survey] The key identifying the type of notification.
 * @param {number} Props.ticketIdActive The active ticket ID associated with the notification.
 * @param {Dispatch} Props.setDestinyStatus Function to set the destiny status for the notification.
 * @param {Dispatch} Props.setNotificationId Function to set the notification ID.
 * @param {Dispatch} Props.setTicketId Function to set the ticket ID.
 * @param {string} Props.title  The title of the notification.
 * @param {Function} Props.setOpenLogoutModal Function to open logout modal
 * @returns {JSX.Element} The rendered notification component.
 */
function Notification({
  advise,
  emmitedDate,
  expireDate,
  handleClose,
  handleReadNotification,
  iconUrl,
  message,
  needsAttention,
  notificationId,
  notificationKey,
  ticketIdActive,
  setDestinyStatus,
  setNotificationId,
  setTicketId,
  setOpenLogoutModal,
  title
}: Props): JSX.Element {
  const { hasPerm } = useGlobalContext();

  const [hasPermissionForAccept, setHasPermissionForAccept] = useState(false);
  const [hasPermissionForReject, setHasPermissionForReject] = useState(false);
  const [disabled, setDisabled] = useState(false);
  const [ticketIdSelected, setTicketIdSelected] = useState<number>(undefined);
  const [isCreateAuditModalOpen, setCreateAuditModal] = useState(false);

  const { t } = useTranslation('global');

  useEffect(() => {
    let acept: AccessElements;
    if (notificationKey === NotificationKey.TicketReviewPendingSerie) {
      acept = AccessElements.TicketPendingBySerie;
    } else if (notificationKey === NotificationKey.ReviewPendingWarehouse) {
      acept = AccessElements.TicketPendingByWarehouse;
    } else if (notificationKey === NotificationKey.ReviewScheduled) {
      acept = AccessElements.TicketUpdateToScheduled;
    }

    setHasPermissionForAccept(hasPerm([acept]));
    if (
      notificationKey === NotificationKey.TicketReviewPendingSerie ||
      notificationKey === NotificationKey.ReviewPendingWarehouse
    ) {
      setHasPermissionForReject(
        hasPerm([AccessElements.TicketUpdateToOnSiteStatus])
      );
    } else if (notificationKey === NotificationKey.ReviewScheduled) {
      setHasPermissionForReject(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [notificationKey]);

  const handleClick = useCallback(() => {
    if (
      notificationKey === NotificationKey.UserRoleChanged &&
      setOpenLogoutModal
    ) {
      setOpenLogoutModal();
    }
    handleReadNotification(notificationId);
  }, [
    handleReadNotification,
    notificationId,
    notificationKey,
    setOpenLogoutModal
  ]);

  const [firstText, ticketId, secondText, clientText, thirdText, statusText] =
    splitByTags(message);

  const { dispatchAction } = useNotificationActions({
    ticketId: +ticketId,
    setTicketId,
    setNotificationId,
    notificationId,
    destinyStatus: null,
    setDestinyStatus,
    handleClose,
    handleReadNotification
  });

  const activeForm = () => {
    setTicketIdSelected(ticketIdActive);
    setCreateAuditModal(true);
  };

  const handleIsCreateChange = (item: boolean) => {
    if (item) {
      setCreateAuditModal(false);
      handleClick();
    }
  };

  return (
    <Box
      onClick={
        !needsAttention
          ? notificationKey !== NotificationKey.Survey && handleClick
          : undefined
      }
      style={styles.buttonBase}
    >
      <Box sx={styles.root}>
        <Box
          sx={{
            display: 'flex',
            flexDirection: {
              xs: 'column',
              sm: 'row'
            }
          }}
        >
          <Box sx={styles.avatarContainer}>
            <Avatar src={iconUrl} sx={styles.icon} />
          </Box>
          <Box
            sx={{
              textAlign: {
                xs: 'justify',
                sm: 'initial'
              }
            }}
          >
            <Typography
              fontSize="12px"
              letterSpacing="0.4px"
              variant="body2"
              gutterBottom
            >
              <strong>{title}</strong>
            </Typography>

            <Typography
              fontSize="12px"
              letterSpacing="0.4px"
              variant="body2"
              gutterBottom
            >
              {firstText} <strong>{ticketId}</strong> {secondText}{' '}
              <strong>{clientText}</strong> {thirdText}{' '}
              <strong>{statusText}</strong>
            </Typography>

            {!expireDate && !ticketId ? (
              <div />
            ) : (
              notificationKey !== NotificationKey.Survey && (
                <Typography fontSize="12px" variant="body2">
                  {t('drawer.surveys.dateFinish')}{' '}
                  {expireDate
                    ? dayjs.utc(expireDate).local().format('DD-MM-YYYY')
                    : '---'}
                </Typography>
              )
            )}

            {needsAttention && advise && (
              <Typography style={{ marginTop: 4 }} variant="body2">
                {advise}
              </Typography>
            )}

            {notificationKey === NotificationKey.Survey && (
              <Box style={styles.actionsButtonsContainer}>
                <Typography fontSize="12px" variant="body2">
                  {t('drawer.surveys.reportSurveys')}
                </Typography>
                <IconButton
                  onClick={() => {
                    activeForm();
                  }}
                  style={styles.btnSendSurvey}
                >
                  {t('drawer.surveys.toAnswer')}
                </IconButton>
              </Box>
            )}
          </Box>
        </Box>

        <Box sx={styles.dateContainer}>
          <Box
            sx={{
              display: {
                xs: 'flex',
                sm: 'grid'
              },
              flexDirection: {
                xs: 'row-reverse'
              }
            }}
          >
            <Typography variant="body2" gutterBottom>
              {dayjs.utc(emmitedDate).local().format('HH:mm:ss')} hrs
            </Typography>

            <Typography
              sx={{ marginRight: { xs: '10px', sm: '0px' } }}
              variant="body2"
              gutterBottom
            >
              {dayjs.utc(emmitedDate).local().format('DD-MM-YYYY')}
            </Typography>
          </Box>
          {needsAttention && (
            <Box style={styles.actionsButtonsContainer}>
              {hasPermissionForReject && (
                <IconButton
                  disabled={disabled}
                  onClick={() => {
                    setDisabled(true);
                    dispatchAction(notificationKey, false);
                  }}
                >
                  <LikeButtonSVG />
                </IconButton>
              )}
              {hasPermissionForAccept && (
                <IconButton
                  disabled={disabled}
                  onClick={() => {
                    setDisabled(true);
                    dispatchAction(notificationKey, true);
                  }}
                >
                  <RejectButtonSVG />
                </IconButton>
              )}
            </Box>
          )}
        </Box>
      </Box>
      <ModalSendFormSurveys
        isCreateAuditModalOpen={isCreateAuditModalOpen}
        onCreateChange={(isCreate: boolean) => handleIsCreateChange(isCreate)}
        sendTicketId={ticketIdSelected}
        setCreateAuditModal={setCreateAuditModal}
      />
    </Box>
  );
}

export default memo(Notification);
